<template>
  <div class="ma-4 px-4">
    <v-sheet outlined>
      <v-data-table
        :headers="programSchemeHeaders"
        :items="program_schemes"
        hide-default-footer
        fixed-header
        class="secondary row-height-50"
      >
        <template #[`item.credits_label`]="{ item }">
          <div
            :class="{
              'credit-mismatch': !item.is_credits_matched,
              'credit-match': item.is_credits_matched,
            }"
          >
            {{ item.credits_label }}
          </div>
        </template>
        <template #[`item.action`]="{ item }">
          <v-icon
            small
            class="mr-2"
            color="accent"
            @click="editProgramScheme(item)"
          >
            mdi-pencil
          </v-icon>
        </template>
      </v-data-table>
    </v-sheet>
    <div class="pt-6">
      <span class="text-5xl font-weight-bold accent--text"> SCHEME TERMS </span>
    </div>
    <searchBarFilter
      @add="(dialog = true), (editMode = false)"
      @updateSearch="(v) => (search = v)"
      @error="errorDownload"
      @uploaded="initialise"
      :uploadButton="true"
      :downloadButton="true"
      @help="showHelp = true"
      headerText="Programme Scheme Term"
      class="mt-2"
      :parentId="Number(program_scheme_id)"
      :parentName="'program_scheme_id'"
      :uploadUrl="'upload_program_scheme_term'"
      :downloadUrl="'download_program_scheme_term'"
      :disable-add="getTermDuration() == program_scheme_terms.length"
      :program_scheme_term_id="Number(program_scheme_id)"
    />
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :search="search"
            :items="program_scheme_terms"
            fixed-header
            :loading="loading"
            class="secondary row-height-50"
          >
            <template #[`item.courses`]="{ item }">
              <v-icon
                class="mr-2"
                color="accent"
                @click="showTermCourses(item)"
              >
                mdi-dots-horizontal
              </v-icon>
            </template>
            <template #[`item.is_stream`]="{ item }">
                {{ item.is_stream ? 'Yes' : 'No' }}
            </template>
            <template #[`item.action`]="{ item }">
              <v-icon small class="mr-2" color="accent" @click="edit(item)">
                mdi-pencil
              </v-icon>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog" width="800" persistent scrollable>
      <formDialog
        v-if="dialog"
        @closeDialog="dialog = false"
        @create="createProgramSchemeTerm"
        @update="updateProgramSchemeTerm"
        :program_scheme_term="program_scheme_term"
        :program_schemes="program_schemes"
        :programs="programs"
        :program_scheme_id="Number(program_scheme_id)"
        :edit="editMode"
        :termNo="program_scheme_terms.length + 1"
      />
    </v-dialog>
    <v-dialog v-model="programSchemeDialog" width="800" persistent scrollable>
      <formProgramSchemeDialog
        v-if="programSchemeDialog"
        @closeDialog="programSchemeDialog = false"
        @update="updateProgramScheme"
        :program_scheme="program_scheme"
        :programs="programs"
        :edit="editProgramSchemeMode"
      />
    </v-dialog>
    <v-dialog v-model="uploadErrorDialog" width="450" persistent>
      <uploadError
        v-if="uploadErrorDialog"
        :errorCsvBlob="errorCsvBlob"
        @downloaded="
          initialise, (uploadErrorDialog = false), (errorCsvBlob = null)
        "
        csvTitle="Program Scheme Term"
      />
    </v-dialog>
    <v-dialog v-model="showHelp" max-width="700px" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          CSV Upload Instructions
          <v-spacer></v-spacer>
          <v-btn small icon @click="showHelp = false">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <h4>📌 General Rules</h4>
          <ul>
            <li>Do not change the column names.</li>
          </ul>
          <h4>📘 Semester/Year</h4>
          <ul>
            <li>Example: <strong>1</strong>, <strong>2</strong>, etc.</li>
          </ul>
          <h4>📘 Nature</h4>
          <ul>
            <li>For core subjects use: <strong>Core</strong></li>
            <li>
              For electives use the format:<br />
              <strong>Elective - 1</strong>, <strong>Elective - 2</strong>,
              <strong>Elective - 3</strong>, etc.
            </li>
          </ul>
          <h4>📘 Handled By</h4>
          <ul>
            <li>Enter the <strong>Partner Code</strong> only</li>
            <li>Example: <strong>TCS</strong>, <strong>IBM</strong>, etc.</li>
          </ul>
          <h4>📘 Assessment Name</h4>
          <ul>
            <li>Example: <strong>I30-E50-SC</strong></li>
          </ul>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import formDialog from "@/components/scheme/program_scheme_term.vue";
import formProgramSchemeDialog from "@/components/scheme/program_scheme.vue";
import searchBarFilter from "@/components/searchBarFilter.vue";
import { durations } from "@/constants/enum";
import {
  programSchemeHeaders,
  programSchemeTermHeaders,
} from "@/constants/headers";
import uploadError from "@/components/uploadError.vue";

export default {
  components: {
    formDialog,
    formProgramSchemeDialog,
    searchBarFilter,
    uploadError,
  },
  data() {
    return {
      durations: durations,
      headers: programSchemeTermHeaders,
      programSchemeHeaders: programSchemeHeaders,
      dialog: false,
      editMode: false,
      programSchemeDialog: false,
      editProgramSchemeMode: false,
      program_scheme_id: null,
      program_scheme_name: "",
      search: "",
      program_scheme_terms: [],
      program_schemes: [],
      programs: [],
      partners: [],
      assessment_schemes: [],
      program_scheme_term: {},
      program_scheme: {},
      loading: false,
      uploadErrorDialog: false,
      errorCsvBlob: null,
      showHelp: false,
    };
  },
  watch: {},
  computed: {},
  created() {
    this.program_scheme_id = this.$route.query.id;
    this.program_scheme_name = this.$route.query.name;
    this.$store.commit("setBreadcrumbs", [
      { text: "Programme Schemes", to: { name: "Programme Schemes" } },
      { text: this.program_scheme_name },
    ]);
    this.initialise();
  },
  methods: {
    async initialise() {
      this.loading = true;
      try {
        const [termRes, schemesRes, programsRes, partnersRes, assessmentRes] =
          await Promise.all([
            this.$axios.get(
              `program_scheme_terms?program_scheme_id=${this.program_scheme_id}`
            ),
            this.$axios.get(`program_schemes/${this.program_scheme_id}`),
            this.$axios.get("programs"),
            this.$axios.get("partners"),
            this.$axios.get("assessment_schemes"),
          ]);
        this.program_scheme_terms = termRes.data.data.sort(
          (a, b) => a.term_no - b.term_no
        );
        this.program_schemes = [schemesRes.data.data];
        this.programs = programsRes.data.data;
        this.partners = partnersRes.data.data;
        this.assessment_schemes = assessmentRes.data.data;
      } catch (err) {
        console.error("Error in initialise():", err);
      } finally {
        this.loading = false;
      }
    },
    async createProgramSchemeTerm(data) {
      await this.$axios
        .post("program_scheme_terms", { program_scheme_term: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Term created successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateProgramSchemeTerm(data) {
      await this.$axios
        .put("program_scheme_terms/" + data.id, { program_scheme_term: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "Term updated successfully",
            color: "success",
          });
          this.resetValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    async updateProgramScheme(data) {
      await this.$axios
        .put("program_schemes/" + data.id, { program_scheme: data })
        .then(() => {
          this.$store.commit("showSnackbar", {
            message: "ProgrammeScheme updated successfully",
            color: "success",
          });
          this.resetProgramSchemeValues();
          this.initialise();
        })
        .catch(async (error) => {
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response
          );
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
    editProgramScheme(item) {
      this.program_scheme = { ...item };
      this.editProgramSchemeMode = true;
      this.programSchemeDialog = true;
    },
    resetProgramSchemeValues() {
      this.editProgramSchemeMode = false;
      this.programSchemeDialog = false;
      this.program_scheme = {};
    },
    edit(item) {
      this.program_scheme_term = { ...item };
      this.editMode = true;
      this.dialog = true;
    },
    resetValues() {
      this.editMode = false;
      this.dialog = false;
      this.program_scheme_term = {};
    },
    showTermCourses(item) {
      this.$router.push({
        path: `/view_pst_courses`,
        query: {
          id: item.id,
          name: this.program_scheme_name,
        },
      });
    },
    getTermDuration() {
      const program_id = this.program_schemes[0]?.program_id;
      return this.programs.find((program) => program.id == program_id)
        ?.duration;
    },
    errorDownload(data) {
      this.$store.commit("showSnackbar", {
        message: "Upload Failed",
        color: "error",
      });
      this.dialog = false;
      this.errorCsvBlob = new Blob([data]);
      this.uploadErrorDialog = true;
    },
  },
};
</script>

<style scoped>
.credit-mismatch {
  /* Red color for the text and background for emphasis */
  color: red;
  border-radius: 4px;
  font-weight: bold;
}

.credit-match {
  /* Green color for successful matching */
  color: #4caf50; /* Green */
  font-weight: bold;
}
</style>
